/**
 * Centralized Timezone Handling for Clinic Studios
 *
 * All dates and times in the application should be displayed in the clinic's
 * local timezone for consistency. This module provides utilities to handle
 * timezone conversion and formatting.
 *
 * IMPORTANT: The clinic operates in Mountain Time (America/Edmonton).
 * This handles both MST (winter) and MDT (summer) automatically.
 */

import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';

// Clinic timezone - Calgary, Alberta (Mountain Time)
// This automatically handles MST/MDT transitions
export const CLINIC_TIMEZONE = 'America/Edmonton';

/**
 * Format a date-only string (YYYY-MM-DD) for display.
 *
 * Date-only strings represent "local dates" at the clinic - they have no
 * timezone component. January 28th means January 28th at the clinic,
 * regardless of the user's timezone.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @param formatStr - date-fns format string (default: 'EEEE, MMM d')
 * @returns Formatted date string
 */
export function formatClinicDate(dateString: string, formatStr: string = 'EEEE, MMM d'): string {
  // Parse the date string as a date in the clinic's timezone
  // We append T12:00:00 (noon) to avoid any DST edge cases at midnight
  const dateInClinicTz = toZonedTime(
    parseISO(`${dateString}T12:00:00`),
    CLINIC_TIMEZONE
  );
  return format(dateInClinicTz, formatStr);
}

/**
 * Format a datetime string for display in clinic timezone.
 *
 * Datetime strings from the API may be in UTC (with Z suffix) or without
 * timezone info. This function ensures consistent display in clinic time.
 *
 * @param dateTimeString - ISO datetime string (e.g., "2026-01-28T09:00:00Z")
 * @param formatStr - date-fns format string (default: 'MMM d, yyyy h:mm a')
 * @returns Formatted datetime string in clinic timezone
 */
export function formatClinicDateTime(
  dateTimeString: string,
  formatStr: string = 'MMM d, yyyy h:mm a'
): string {
  return formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, formatStr);
}

/**
 * Format just the date portion of a datetime in clinic timezone.
 *
 * @param dateTimeString - ISO datetime string
 * @param formatStr - date-fns format string (default: 'EEEE, MMM d, yyyy')
 * @returns Formatted date string
 */
export function formatClinicDateFromDateTime(
  dateTimeString: string,
  formatStr: string = 'EEEE, MMM d, yyyy'
): string {
  return formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, formatStr);
}

/**
 * Format just the time portion of a datetime in clinic timezone.
 *
 * @param dateTimeString - ISO datetime string
 * @param formatStr - date-fns format string (default: 'h:mm a')
 * @returns Formatted time string
 */
export function formatClinicTime(
  dateTimeString: string,
  formatStr: string = 'h:mm a'
): string {
  return formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, formatStr);
}

/**
 * Format a time slot (hour and minute) for display.
 * Used for availability calendar time labels.
 *
 * @param hour - Hour (0-23)
 * @param minute - Minute (0 or 30)
 * @returns Formatted time string (e.g., "9:00 AM")
 */
export function formatTimeSlot(hour: number, minute: number = 0): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
}

/**
 * Get a short date format for compact displays.
 *
 * @param dateString - Date string (YYYY-MM-DD) or datetime string
 * @returns Short formatted date (e.g., "Jan 28")
 */
export function formatShortDate(dateString: string): string {
  // Determine if this is a date-only or datetime string
  if (dateString.includes('T')) {
    return formatInTimeZone(parseISO(dateString), CLINIC_TIMEZONE, 'MMM d');
  }
  return formatClinicDate(dateString, 'MMM d');
}

/**
 * Format a date for display in booking/appointment contexts.
 *
 * @param dateString - Date string (YYYY-MM-DD)
 * @returns Formatted date (e.g., "Tuesday, Jan 28")
 */
export function formatBookingDate(dateString: string): string {
  return formatClinicDate(dateString, 'EEEE, MMM d');
}

/**
 * Format a datetime for booking confirmation display.
 *
 * @param dateTimeString - ISO datetime string
 * @returns Object with formatted date and time
 */
export function formatBookingDateTime(dateTimeString: string): { date: string; time: string } {
  return {
    date: formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, 'EEEE, MMMM d, yyyy'),
    time: formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, 'h:mm a'),
  };
}

/**
 * Calculate end time given start time and duration.
 *
 * @param startTimeString - ISO datetime string for start
 * @param durationMinutes - Duration in minutes
 * @returns Formatted end time string
 */
export function formatEndTime(startTimeString: string, durationMinutes: number): string {
  const startDate = parseISO(startTimeString);
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  return formatInTimeZone(endDate, CLINIC_TIMEZONE, 'h:mm a');
}

/**
 * Format a relative date description.
 *
 * @param dateTimeString - ISO datetime string
 * @returns Object with short date format and relative description
 */
export function formatRelativeBookingDate(dateTimeString: string): {
  date: string;
  time: string;
  weekday: string;
} {
  return {
    weekday: formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, 'EEE'),
    date: formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, 'MMM d'),
    time: formatInTimeZone(parseISO(dateTimeString), CLINIC_TIMEZONE, 'h:mm a'),
  };
}

/**
 * Convert clinic local time components to UTC ISO string.
 *
 * Used when creating bookings - the availability slots are in clinic local time,
 * and we need to send UTC to the API.
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param hour - Hour (0-23) in clinic local time
 * @param minute - Minute (0 or 30)
 * @returns ISO datetime string in UTC (e.g., "2026-01-28T17:00:00.000Z")
 */
export function clinicTimeToUTC(date: string, hour: number, minute: number): string {
  // Construct a datetime string representing the clinic local time
  const hourStr = hour.toString().padStart(2, '0');
  const minuteStr = minute.toString().padStart(2, '0');
  const clinicLocalDateTime = `${date}T${hourStr}:${minuteStr}:00`;

  // Parse as clinic timezone and convert to UTC
  const utcDate = fromZonedTime(clinicLocalDateTime, CLINIC_TIMEZONE);

  // Return as ISO string (which is always UTC with Z suffix)
  return utcDate.toISOString();
}

/**
 * Convert a zoneless datetime-local value (e.g. "2026-06-01T14:00" from an
 * <input type="datetime-local">) to a UTC ISO string, interpreting the
 * wall-clock value as clinic local time rather than the browser's timezone.
 */
export function clinicDateTimeToUTC(localDateTime: string): string {
  return fromZonedTime(localDateTime, CLINIC_TIMEZONE).toISOString();
}
